import React, { useCallback, useState } from 'react';
import Helmet from 'react-helmet';
import { useLocation, useParams, Route, Routes } from 'react-router-dom';
import { Container, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useQuery } from '@apollo/client';

import { Breadcrumbs, BurgerMenu, Header, Main } from '../../components';
import { AUTH_QUERY, GET_MY_FIND_QUERY } from '../../gql';
import { FindImages } from '../../components/FindImages';
import { FindSettings } from '../../components/FindSettings';
import { FindMenu } from './FindMenu';
import { FindActionBar } from './FindActionBar';
import { useIntl } from 'react-intl';

const Find = React.memo(() => {
  const intl = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [saveDisabled, setSaveDisabled] = useState(true);

  const { findId, siteId } = useParams();
  const { data: authData } = useQuery(AUTH_QUERY);
  const { called, data, loading } = useQuery(GET_MY_FIND_QUERY, {
    variables: { findId },
    fetchPolicy: 'cache-first',
    skip: !findId,
  });

  const find = data?.getMyFind;
  const viewerId = authData?.viewer?.user?.id;
  const isShared = Boolean(
    find?.createdBy && viewerId && find.createdBy !== viewerId
  );
  const showSave = !location.pathname.endsWith('/images');
  const handleSaveDisabledChange = useCallback((disabled: boolean) => {
    setSaveDisabled(disabled);
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Helmet
        title={intl.formatMessage({
          id: 'find.pageTitle',
          defaultMessage: 'Find | Acolyte',
        })}
      />
      <Header mainIcon={isMobile && <BurgerMenu menu={<FindMenu />} />} />
      <Container maxWidth="xl">
        <Stack direction="row">
          {!isMobile && <FindMenu />}
          <Stack flexGrow={1} gap={1}>
            <Stack
              direction={isMobile ? 'column' : 'row'}
              justifyContent="space-between"
              alignItems={isMobile ? 'flex-start' : 'center'}
            >
              <Breadcrumbs />
              {findId && (
                <FindActionBar
                  createdBy={find?.createdBy}
                  findId={findId}
                  saveDisabled={loading || !find || saveDisabled}
                  shared={isShared}
                  showSave={showSave}
                  siteId={siteId || find?.siteId}
                />
              )}
            </Stack>
            <Main>
              <Routes>
                <Route
                  index
                  element={
                    <FindSettings
                      findId={findId}
                      called={called}
                      loading={loading}
                      find={find}
                      onSaveDisabledChange={handleSaveDisabledChange}
                    />
                  }
                />
                <Route
                  path="images"
                  element={
                    <FindImages
                      findId={findId}
                      called={called}
                      loading={loading}
                      find={find}
                    />
                  }
                />
              </Routes>
            </Main>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
});

export { Find };
